/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import { IncomingHttpHeaders, ServerResponse } from 'http';
import { QueryParams } from '../constants/queryParams';
import { existPropertyNumbers } from '../utils/objectUtil';
import {
  createCookieStr,
  getCookie,
  pushSetCookie,
} from '../utils/httpCookieUtil';
import { createParam } from './ogp';

export const MAX_SEARCH_CONDITION = 3;
const COOKIE_SEARCH_CONDITION_KEY = 'search_condition';
const CONDITION_KEYS = [
  'oc',
  'ar',
  'pr',
  'ci',
  'wa',
  'rt',
  'st',
  'ind',
  'ha',
  'kwc',
  'k',
  'niwc',
  'ni',
  'ne',
  'es',
  'op',
  'tp',
];

export const setSearchConditionToCookiesOnJobSearchList = (
  query: QueryParams,
  headers: IncomingHttpHeaders,
  res: ServerResponse,
  isLogin: boolean
) => {
  const condKeys = [...CONDITION_KEYS];
  const cond = extractMap(query, condKeys);

  // エンコードする
  if (cond.k) {
    cond.k = encodeURIComponent(cond.k ?? '');
  }
  if (cond.ni) {
    cond.ni = encodeURIComponent(cond.ni ?? '');
  }

  // 以下条件時、設定しない
  // 検索条件が存在しない
  // tpのみかつ、tp===1の時
  const existsNum = existPropertyNumbers(cond);
  if (existsNum === 0 || (existsNum === 1 && cond.tp && cond.tp === '1'))
    return;

  // MAX_SEARCH_CONDITION - 1 までを取得
  const prevCondList: string[] = [];
  const cookie = headers.cookie ?? '';
  [...Array(MAX_SEARCH_CONDITION)].forEach((_, n) => {
    const key = COOKIE_SEARCH_CONDITION_KEY + (n + 1);
    const condCookie = getCookie(cookie, key);
    if (condCookie) {
      // Cookie設定値の整合性チェック
      let isDelete = false;
      // cond: [[ar,1],[pr,1]...]
      const splitParams = condCookie.split('&').map((v) => v.split('='));
      isDelete = splitParams.some((keyValue, idx) => {
        // eslint-disable-next-line no-shadow
        const key = keyValue[0];
        // 「キー=値」の形式ではないパラメータが1つでもあればCookieリストから除外
        if (
          keyValue.length !== 2 ||
          // 検索条件のキーとして有効な値が設定されてなければCookieリストから除外
          (key !== 'dt' && key !== 'db' && !condKeys.includes(key)) ||
          // 検索条件の最後に日付が入っていなければCookieリストから除外
          (idx === splitParams.length - 1 && key !== 'dt')
        ) {
          return true;
        }
      });
      if (isDelete) return;
      // Cookieに保存されている検索条件と同じ条件がないかをチェック
      // 検索条件が一致したものはCookieリストから削除
      const isDiff = splitParams.some(([k, v]) => {
        if (k !== 'dt' && k !== 'db' && cond[k] !== v) return true;
      });

      if (isDiff) prevCondList.push(condCookie);
    }
  });

  // 1,2→2,3に移動
  // 最新の検索日時をCookieに保存する(latest_search)
  const latestSearch = getFullTimeWithSeparator();
  // 現在の検索条件を1にセット以下1作成
  let newCond = createParam(cond).slice(1);
  // dbパラムをセット
  if (isLogin) newCond += '&db=1';
  // dtに登録日をセット
  newCond += `&dt=${getFullTime()}`;

  const setCookie = [
    createCookieStr('latest_search', latestSearch, {
      'max-age': 2 * 365 * 24 * 60 * 60,
    }),
    ...createHistoryCookieList(newCond, prevCondList),
  ];

  pushSetCookie(res, setCookie);
  return newCond;
};

export const setSearchConditionCookieToHeaderOnCareerRecommendJobList = (
  query: QueryParams,
  headers: IncomingHttpHeaders,
  isLogin: boolean
) => {
  const condKeys = [...CONDITION_KEYS];
  const cond = extractMap(query, condKeys);
  // エンコード
  if (cond.k) cond.k = encodeURIComponent(cond.k ?? '');
  if (cond.ni) cond.ni = encodeURIComponent(cond.ni ?? '');

  // 以下条件時、設定しない
  // 検索条件が存在しない
  // tpのみかつ、tp===1の時
  const existsNum = existPropertyNumbers(cond);
  if (existsNum === 0 || (existsNum === 1 && cond.tp && cond.tp === '1'))
    return;

  // 現在の検索条件を1にセット以下1作成
  let newCond = createParam(cond).slice(1);
  // dbパラムをセット
  if (isLogin) newCond += '&db=1';
  // dtに登録日をセット
  newCond += `&dt=${getFullTime()}`;

  const key = `${COOKIE_SEARCH_CONDITION_KEY}1`;
  const cookie = createCondHistoryCookie(key, newCond);
  return {
    ...headers,
    cookie,
  };
};

export function createHistoryCookieList(
  newCond: string,
  prevCondList: string[]
) {
  const list = [newCond, ...prevCondList];
  return list.flatMap((v, i) => {
    if (i > MAX_SEARCH_CONDITION - 1) return [];
    const key = COOKIE_SEARCH_CONDITION_KEY + (i + 1);
    return [
      createCookieStr(key, '', {
        'max-age': -1,
      }),
      createCondHistoryCookie(key, v),
    ];
  });
}

export function createCondHistoryCookie(key: string, value: string) {
  const date = new Date();
  const cookie = createCookieStr(key, value, {
    expires: new Date(date.setFullYear(date.getFullYear() + 5)),
  });
  return cookie;
}

export function getFullTime() {
  // yyyyMMddHHmmss
  return (
    new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .format(new Date())
      // eslint-disable-next-line no-useless-escape
      .replace(/[' '|':'|'\/']/g, '')
  );
}

export function getFullTimeWithSeparator() {
  // yyyy-MM-dd'T'HH:mm:ss'Z'
  return `${new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .format(new Date())
    .replace(' ', 'T')
    .replaceAll('/', '-')}Z`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractMap<M = { [T in any]: any }>(map: M, keys: string[]) {
  return keys.reduce((r, i) => {
    if (map[i]) {
      r[i] = map[i];
      return r;
    }
    return r;
  }, {} as M);
}

export const createPrevSearchCondHeader = (
  headers: IncomingHttpHeaders,
  res: ServerResponse
) => {
  const setCookie = res.getHeader('Set-Cookie');
  // eslint-disable-next-line no-useless-concat
  const regExp = new RegExp(`${COOKIE_SEARCH_CONDITION_KEY}1` + `=([^;]+)`);
  const prevrevSearchCondHeader = {
    ...headers,
    cookie: Array.isArray(setCookie)
      ? setCookie.find((v) => v.match(regExp))
      : undefined,
  };
  return prevrevSearchCondHeader;
};
